import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Crown, ArrowRight, ExternalLink } from "lucide-react";

/**
 * Editor Pick — the current #1 leaderboard contributor's newest deal.
 * Renders as an editorial spotlight strip on the homepage.
 */
export default function EditorPick() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/community/champion");
        setData(r.data);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  if (!data?.deal || !data?.champion) return null;
  const { champion, deal } = data;

  return (
    <section className="max-w-7xl mx-auto px-6 pt-8" data-testid="editor-pick">
      <div className="border-2 border-black bg-white">
        <div className="grid grid-cols-1 md:grid-cols-12">
          <div className="md:col-span-5 p-6 md:p-8 flex flex-col justify-between border-b md:border-b-0 md:border-r border-black/10">
            <div>
              <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-white bg-[#111111] px-2 py-1 mb-4">
                <Crown className="w-3.5 h-3.5" strokeWidth={2} /> Editor Pick · 24h Spotlight
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-black/50 mb-2">
                Chosen by #1 contributor
              </div>
              <h3 className="font-serif font-black text-3xl md:text-4xl leading-tight tracking-tighter" data-testid="editor-pick-title">
                {deal.title}
              </h3>
              <p className="text-sm text-black/60 mt-3 leading-relaxed">{deal.description}</p>
              <div className="mt-4 text-xs font-mono uppercase tracking-widest text-black/70 flex items-center gap-2 flex-wrap">
                <span>Curated by</span>
                <span className="text-[#E63946] font-bold">{champion.name}</span>
                <span className="inline-flex items-center gap-1 bg-[#111111] text-white px-1.5 py-0.5">
                  <span aria-hidden="true">★</span> Top Contributor · {champion.approved_count} approved
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-6">
              <a
                href={deal.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[#E63946]"
                data-testid="editor-pick-cta"
              >
                Shop the pick <ExternalLink className="w-3.5 h-3.5" strokeWidth={2} />
              </a>
              <Link to="/leaderboard" className="text-xs font-bold uppercase tracking-widest hover:text-[#E63946] flex items-center gap-1" data-testid="editor-pick-board-link">
                Meet the board <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
            </div>
          </div>
          <div className="md:col-span-7 relative bg-black/5 min-h-[220px] aspect-[16/9] md:aspect-auto">
            {deal.image_url ? (
              <img src={deal.image_url} alt={deal.title} className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-black/15 font-serif text-[10rem] leading-none">◇</div>
            )}
            {deal.discount && (
              <div className="absolute top-4 left-4 bg-[#E63946] text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5">
                {deal.discount}
              </div>
            )}
            <div className="absolute bottom-4 right-4 text-[10px] font-mono uppercase tracking-widest bg-white/90 backdrop-blur px-2 py-1">
              {deal.category} · {deal.merchant}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
